import {State, Action, Selector, StateContext} from '@ngxs/store';
import {
  CreateDataset,
  GetAllMlInfo,
  GetMakeAndModelData,
  GetMlStats, GetSimulationsMetadata, GetSimulationsResponse,
  GetTrainingHistory,
  SimulatePrice,
  TrainModel
} from './ml.actions';
import {Injectable} from '@angular/core';
import {MlService} from '@shared/service/ml/ml.service';
import {SimulateBody, ServerResponse} from '@shared/interfaces';
import {ToastService} from '@app/service/util/toast.service';
import {DataSnapshot} from '@angular/fire/compat/database/interfaces';
import {RoutingService} from '@app/service/routing/routing.service';

export interface MlStateModel {
  mlStats: any;
  mlModelInfo: any[];
  totalListings: number;
  totalMakes: number;
  totalModels: number;
  carModelData: any[];
  runLogs: { [k: string]: any[] };
  simulationsResponse: { [k: string]: any[] };
  simulationsMetadata: { [k: string]: any[] };
}

@State<MlStateModel>({
  name: 'ml',
  defaults: {
    mlStats: {},
    mlModelInfo: [],
    totalListings: 0,
    totalMakes: 0,
    totalModels: 0,
    carModelData: [],
    runLogs: {},
    simulationsResponse: {},
    simulationsMetadata: {}
  }
})
@Injectable()
export class MlState {
  constructor(private mlService: MlService, private toast: ToastService, private routingService: RoutingService) {
  }

  @Selector()
  static getMlStats(state: MlStateModel) {
    return state.mlStats;
  }

  @Selector()
  static getMlModelInfo(state: MlStateModel) {
    return state.mlModelInfo;
  }

  @Selector()
  static getTotalListings(state: MlStateModel) {
    return state.totalListings;
  }

  @Selector()
  static getTotalMakes(state: MlStateModel) {
    return state.totalMakes;
  }

  @Selector()
  static getTotalModels(state: MlStateModel) {
    return state.totalModels;
  }

  @Selector()
  static getCarModelData(state: MlStateModel) {
    return state.carModelData;
  }

  @Selector()
  static getSimulationsMetadata(state: MlStateModel) {
    return state.simulationsMetadata;
  }

  @Selector()
  static getSimulationsResponse(state: MlStateModel) {
    return state.simulationsResponse;
  }

  @Selector()
  static getRunLogs(state: MlStateModel) {
    return state.runLogs;
  }

  @Action(GetMlStats)
  getMlStats({patchState}: StateContext<MlStateModel>) {
    return this.mlService.getMlStats((snapshot: DataSnapshot) => {
      if (!snapshot.exists()) {
        return this.toast.info(`No Ml Stats found`);
      }
      const mlStats = snapshot.val();
      mlStats['carsWithMlModelsCount'] = Object.keys(mlStats['carsWithMlModels']).length;
      mlStats['mlModelsTrainedCount'] = Object.keys(mlStats['mlModelsTrained']).length;
      mlStats['carsWithListingsCount'] = Object.keys(mlStats['carsWithListings']).length;
      patchState({
        mlStats
      });
    });
  }

  @Action(GetMakeAndModelData)
  getMakeAndModelData({patchState}: StateContext<MlStateModel>) {
    return this.mlService.getAllMakeAndModelData()
      .then((response: ServerResponse) => {
        const data = response.data;
        const totalListings = data.totalListings;
        const totalMakes = data.totalMakes;
        const totalModels = data.totalModels;
        const carModelData = data.modelData;
        patchState({
          totalListings,
          totalMakes,
          totalModels,
          carModelData
        });
      })
      .catch(err => {
        this.toast.error(err.message);
      });
  }

  @Action(GetAllMlInfo)
  getCarMlInfo({patchState}: StateContext<MlStateModel>) {
    return this.mlService.getMlModelInfo((snapshot: DataSnapshot) => {
      if (!snapshot.exists()) {
        return this.toast.info(`No Ml Info found`);
      }
      const result = [];
      const mlModelInfo = snapshot.val();
      const carMakesData = mlModelInfo['carMakes'];
      for (const makeName in carMakesData) {
        const carMake = makeName;
        const carModelsData = mlModelInfo['carMakes'][makeName]['carModels'];

        for (const modelName in carModelsData) {
          const modelInfo = carModelsData[modelName];
          result.push({
            uid: `${carMake}_${modelName}`,
            carMake: carMake.toUpperCase(),
            carModel: modelName.toUpperCase(),
            ...modelInfo
          });
        }
      }
      patchState({
        mlModelInfo: result
      });
    });
  }

  @Action(TrainModel)
  trainModel({patchState}: StateContext<MlStateModel>, {payload}: TrainModel) {
    return this.mlService.trainModel(payload)
      .then((response: ServerResponse) => {
        this.toast.response(response.type, response.message, response.subtext, 10000);

        if (response.data) {
          console.log(`RESPONSE DATA ${response.data}`);
        }
      })
      .catch(err => {
        this.toast.error(err.message);
      });
  }

  @Action(GetTrainingHistory)
  getTrainingHistory({getState, patchState}: StateContext<MlStateModel>, {payload}: GetTrainingHistory) {
    const key = `${payload.carMake.toLowerCase()}_${payload.carModel.toLowerCase()}`;
    const trainingHistoryByKey = getState().runLogs[key];
    if (trainingHistoryByKey && trainingHistoryByKey.length > 0) {
      return Promise.resolve(true);
    }
    return this.mlService.getTrainingHistory(payload.carMake, payload.carModel, (snapshot: DataSnapshot) => {
      if (!snapshot.exists()) {
        return this.toast.info(`No training logs found for ${payload.carMake} ${payload.carModel}`);
      }
      const serverRunLogs = snapshot.val();
      const arrResult = Object.values(serverRunLogs);

      const runLogs = Object.assign({}, getState().runLogs, {[key]: arrResult.reverse()});
      patchState({
        runLogs
      });
    });
  }

  @Action(SimulatePrice)
  simulatePrice({getState, patchState, dispatch}: StateContext<MlStateModel>, {payload}: SimulatePrice) {
    const newPayload: SimulateBody = {
      car_year: payload.carYear,
      car_make: payload.carMake,
      car_model: payload.carModel,
      model_type: payload.modelType,
      mileage: payload.mileage,
      zip_code: payload.zipCode,
    };
    return this.mlService.simulatePrice(newPayload)
      .then((response: ServerResponse) => {
        const timestamp = response.data.timestamp;
        this.toast.response(response.type, response.message, response.subtext, 10000);

        this.routingService.navigateToPath(['view-simulated-data'], {timestamp}, {
          queryParamsHandling: 'merge'
        });
      })
      .catch(err => {
        this.toast.error(err.message);
      });
  }

  @Action(GetSimulationsMetadata)
  getSimulationsMetadata({getState, patchState}: StateContext<MlStateModel>) {
    const predMeta = getState().simulationsMetadata;
    if (predMeta && Object.keys(predMeta).length > 0) {
      return Promise.resolve(true);
    }
    return this.mlService.getAllSimulationsMetadata((snapshot: DataSnapshot) => {
      if (!snapshot.exists()) {
        return this.toast.info(`No simulations metadata found`);
      }
      patchState({
        simulationsMetadata: snapshot.val()
      });
    });
  }

  @Action(GetSimulationsResponse)
  getSimulationsResponse({getState, patchState}: StateContext<MlStateModel>, {payload}: GetSimulationsResponse) {
    const timestamp = payload.timestamp;
    const dataByTimestamp = getState().simulationsResponse[timestamp];
    if (dataByTimestamp && Object.keys(dataByTimestamp).length > 0) {
      return Promise.resolve(true);
    }
    return this.mlService.getSimulationsByTimestamp(timestamp, (snapshot: DataSnapshot) => {
      if (!snapshot.exists()) {
        return this.toast.info(`No simulations response found for ${timestamp}`);
      }
      const simulations = snapshot.val();

      patchState({
        simulationsResponse: Object.assign({}, getState().simulationsResponse, {[timestamp]: simulations})
      });
    });
  }

  @Action(CreateDataset)
  createDataset({getState, patchState}: StateContext<MlStateModel>, {payload}: CreateDataset) {
    return this.mlService.createDataset(payload)
      .then((response: ServerResponse) => {
        this.toast.response(response.type, response.message, response.subtext, 10000);

        if (response.data) {
          console.log(`RESPONSE DATA ${response.data}`);
        }
      })
      .catch(err => {
        this.toast.error(err.message);
      });
  }
}
